// AUTO-GENERATED by scripts/fetch-school-cut-off-points.ts — do not edit manually
// Source: MOE School Finder (https://www.moe.gov.sg/schoolfinder)
// PSLE cohort: 2025 · S1 intake: 2026 · Verified: 2026-08-12
// Schools with a published cut-off point: 139

/**
 * PSLE cut-off points per secondary school, as published by MOE School Finder.
 *
 * What: The score range of the first and last student posted to each school,
 *       per posting group, split by affiliated and non-affiliated admission.
 * Why:  School pages rank on the first page for '<school> cut off point' but the
 *       snippet never contains the number, so the click goes elsewhere. This is
 *       the number.
 * When: MOE refreshes School Finder after each S1 posting exercise (December).
 *       Re-run the generator then; every constant below moves with the data.
 *
 * MOE publishes only the most recent posting exercise, so there is no history
 * here. A school with no published cut-off point has no entry at all — read a
 * missing slug as "MOE published nothing", never as zero, and render no
 * cut-off section for it.
 */

/** Higher Chinese Language grade, used as the SAP-school admission tie-break. */
export type HclGrade = 'D' | 'M' | 'P';

export interface CutOffBound {
  /** PSLE Achievement Level total, 4 (best) to 30. */
  score: number;
  /**
   * HCL grade of this student. MOE publishes it only for SAP schools, and only
   * when the student took and passed Higher Chinese.
   */
  hclGrade?: HclGrade;
}

export interface CutOffRange {
  /** First student posted — the strongest score the school admitted. */
  first: CutOffBound;
  /** Last student posted. This score is the school's cut-off point. */
  last: CutOffBound;
  /**
   * True when the school still had vacancies after posting. MOE then reports
   * the cut-off as the weakest score it could have admitted rather than the
   * score of a real student, so this must not be read as "hard to get into".
   */
  unfilledVacancies?: boolean;
}

export type PostingGroupKey =
  | 'integratedProgramme'
  | 'postingGroup3'
  | 'postingGroup2'
  | 'postingGroup1';

export type PostingGroupCutOffs = Partial<Record<PostingGroupKey, CutOffRange>>;

export interface SchoolCutOffPoints {
  /** Matches `SchoolRecord.slug` in `schoolData.ts`. */
  slug: string;
  moeSchoolCode: string;
  nonAffiliated: PostingGroupCutOffs;
  /** Present only for schools that admit affiliated pupils. */
  affiliated?: PostingGroupCutOffs;
}

/** PSLE cohort these cut-off points come from. */
export const CUT_OFF_PSLE_YEAR = 2025;

/** Secondary 1 intake these cut-off points admitted. */
export const CUT_OFF_S1_INTAKE_YEAR = 2026;

/** Date the dataset was last read back from MOE School Finder. */
export const CUT_OFF_VERIFIED_ON = '2026-08-12';

export const CUT_OFF_SOURCE_NAME = 'MOE School Finder';
export const CUT_OFF_SOURCE_URL = 'https://www.moe.gov.sg/schoolfinder';

/** Posting groups from most to least selective — the order to render them in. */
export const POSTING_GROUP_ORDER: readonly PostingGroupKey[] = [
  'integratedProgramme',
  'postingGroup3',
  'postingGroup2',
  'postingGroup1',
];

export const POSTING_GROUP_LABELS: Record<PostingGroupKey, string> = {
  integratedProgramme: 'Integrated Programme',
  postingGroup3: 'Posting Group 3',
  postingGroup2: 'Posting Group 2',
  postingGroup1: 'Posting Group 1',
};

export const SCHOOL_CUT_OFF_POINTS: Record<string, SchoolCutOffPoints> = {
  'admiralty-secondary-school': {
    slug: 'admiralty-secondary-school',
    moeSchoolCode: '3072',
    nonAffiliated: {
      postingGroup3: { first: { score: 16 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'ahmad-ibrahim-secondary-school': {
    slug: 'ahmad-ibrahim-secondary-school',
    moeSchoolCode: '3201',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'anderson-secondary-school': {
    slug: 'anderson-secondary-school',
    moeSchoolCode: '3001',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'ang-mo-kio-secondary-school': {
    slug: 'ang-mo-kio-secondary-school',
    moeSchoolCode: '3026',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'anglican-high-school': {
    slug: 'anglican-high-school',
    moeSchoolCode: '7101',
    nonAffiliated: {
      postingGroup3: { first: { score: 4 }, last: { score: 12 } },
    },
  },
  'anglo-chinese-school-barker-road': {
    slug: 'anglo-chinese-school-barker-road',
    moeSchoolCode: '7032',
    nonAffiliated: {
      postingGroup3: { first: { score: 6 }, last: { score: 9 } },
      postingGroup2: { first: { score: 21 }, last: { score: 21 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 21 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'anglo-chinese-school-independent': {
    slug: 'anglo-chinese-school-independent',
    moeSchoolCode: '7001',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 7 } },
      postingGroup3: { first: { score: 6 }, last: { score: 8 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 13 } },
    },
  },
  'assumption-english-school': {
    slug: 'assumption-english-school',
    moeSchoolCode: '7002',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 29 } },
    },
  },
  'bartley-secondary-school': {
    slug: 'bartley-secondary-school',
    moeSchoolCode: '3002',
    nonAffiliated: {
      postingGroup3: { first: { score: 16 }, last: { score: 21 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'beatty-secondary-school': {
    slug: 'beatty-secondary-school',
    moeSchoolCode: '3003',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'bedok-green-secondary-school': {
    slug: 'bedok-green-secondary-school',
    moeSchoolCode: '3069',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'bedok-south-secondary-school': {
    slug: 'bedok-south-secondary-school',
    moeSchoolCode: '3027',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'bedok-view-secondary-school': {
    slug: 'bedok-view-secondary-school',
    moeSchoolCode: '3225',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'bendemeer-secondary-school': {
    slug: 'bendemeer-secondary-school',
    moeSchoolCode: '3021',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'boon-lay-secondary-school': {
    slug: 'boon-lay-secondary-school',
    moeSchoolCode: '3224',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'bowen-secondary-school': {
    slug: 'bowen-secondary-school',
    moeSchoolCode: '3043',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'broadrick-secondary-school': {
    slug: 'broadrick-secondary-school',
    moeSchoolCode: '3202',
    nonAffiliated: {
      postingGroup3: { first: { score: 5 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'bukit-batok-secondary-school': {
    slug: 'bukit-batok-secondary-school',
    moeSchoolCode: '3044',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 15 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
  },
  'bukit-merah-secondary-school': {
    slug: 'bukit-merah-secondary-school',
    moeSchoolCode: '3203',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'bukit-panjang-govt-high-school': {
    slug: 'bukit-panjang-govt-high-school',
    moeSchoolCode: '3204',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
  },
  'bukit-view-secondary-school': {
    slug: 'bukit-view-secondary-school',
    moeSchoolCode: '3040',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'canberra-secondary-school': {
    slug: 'canberra-secondary-school',
    moeSchoolCode: '3621',
    nonAffiliated: {
      postingGroup3: { first: { score: 17 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'catholic-high-school': {
    slug: 'catholic-high-school',
    moeSchoolCode: '7102',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4, hclGrade: 'D' }, last: { score: 7, hclGrade: 'M' } },
      postingGroup3: { first: { score: 6, hclGrade: 'D' }, last: { score: 8, hclGrade: 'M' } },
    },
    affiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 12 } },
    },
  },
  'cedar-girls-secondary-school': {
    slug: 'cedar-girls-secondary-school',
    moeSchoolCode: '3004',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 9 } },
      postingGroup3: { first: { score: 6 }, last: { score: 9 } },
    },
  },
  'changkat-changi-secondary-school': {
    slug: 'changkat-changi-secondary-school',
    moeSchoolCode: '3402',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'chij-katong-convent': {
    slug: 'chij-katong-convent',
    moeSchoolCode: '7008',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
    affiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'chij-secondary-toa-payoh': {
    slug: 'chij-secondary-toa-payoh',
    moeSchoolCode: '7004',
    nonAffiliated: {
      postingGroup3: { first: { score: 6 }, last: { score: 10 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 29 } },
    },
  },
  'chij-st-josephs-convent': {
    slug: 'chij-st-josephs-convent',
    moeSchoolCode: '7019',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'chij-st-nicholas-girls-school': {
    slug: 'chij-st-nicholas-girls-school',
    moeSchoolCode: '7118',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4, hclGrade: 'D' }, last: { score: 7, hclGrade: 'M' } },
      postingGroup3: { first: { score: 6, hclGrade: 'M' }, last: { score: 8, hclGrade: 'M' } },
    },
    affiliated: {
      postingGroup3: { first: { score: 7, hclGrade: 'M' }, last: { score: 14 } },
    },
  },
  'chij-st-theresas-convent': {
    slug: 'chij-st-theresas-convent',
    moeSchoolCode: '7023',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
    affiliated: {
      postingGroup3: { first: { score: 9 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'christ-church-secondary-school': {
    slug: 'christ-church-secondary-school',
    moeSchoolCode: '7025',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 17 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'chua-chu-kang-secondary-school': {
    slug: 'chua-chu-kang-secondary-school',
    moeSchoolCode: '3055',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'chung-cheng-high-school-main': {
    slug: 'chung-cheng-high-school-main',
    moeSchoolCode: '7104',
    nonAffiliated: {
      postingGroup3: { first: { score: 5, hclGrade: 'D' }, last: { score: 10, hclGrade: 'M' } },
    },
  },
  'chung-cheng-high-school-yishun': {
    slug: 'chung-cheng-high-school-yishun',
    moeSchoolCode: '7105',
    nonAffiliated: {
      postingGroup3: { first: { score: 6 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'clementi-town-secondary-school': {
    slug: 'clementi-town-secondary-school',
    moeSchoolCode: '3029',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'commonwealth-secondary-school': {
    slug: 'commonwealth-secondary-school',
    moeSchoolCode: '3012',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'compassvale-secondary-school': {
    slug: 'compassvale-secondary-school',
    moeSchoolCode: '3622',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'crescent-girls-school': {
    slug: 'crescent-girls-school',
    moeSchoolCode: '3005',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
    },
  },
  'damai-secondary-school': {
    slug: 'damai-secondary-school',
    moeSchoolCode: '3056',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'deyi-secondary-school': {
    slug: 'deyi-secondary-school',
    moeSchoolCode: '3228',
    nonAffiliated: {
      postingGroup3: { first: { score: 9 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'dunearn-secondary-school': {
    slug: 'dunearn-secondary-school',
    moeSchoolCode: '3503',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'dunman-high-school': {
    slug: 'dunman-high-school',
    moeSchoolCode: '3101',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4, hclGrade: 'D' }, last: { score: 8, hclGrade: 'M' } },
    },
  },
  'dunman-secondary-school': {
    slug: 'dunman-secondary-school',
    moeSchoolCode: '3207',
    nonAffiliated: {
      postingGroup3: { first: { score: 6 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'east-spring-secondary-school': {
    slug: 'east-spring-secondary-school',
    moeSchoolCode: '3609',
    nonAffiliated: {
      postingGroup3: { first: { score: 16 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 29 } },
    },
  },
  'edgefield-secondary-school': {
    slug: 'edgefield-secondary-school',
    moeSchoolCode: '3075',
    nonAffiliated: {
      postingGroup3: { first: { score: 5 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'evergreen-secondary-school': {
    slug: 'evergreen-secondary-school',
    moeSchoolCode: '3623',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 15 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
  },
  'fairfield-methodist-school-secondary': {
    slug: 'fairfield-methodist-school-secondary',
    moeSchoolCode: '7309',
    nonAffiliated: {
      postingGroup3: { first: { score: 6 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 21 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 6 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'fuhua-secondary-school': {
    slug: 'fuhua-secondary-school',
    moeSchoolCode: '3614',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'gan-eng-seng-school': {
    slug: 'gan-eng-seng-school',
    moeSchoolCode: '3006',
    nonAffiliated: {
      postingGroup3: { first: { score: 9 }, last: { score: 15 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'geylang-methodist-school-secondary': {
    slug: 'geylang-methodist-school-secondary',
    moeSchoolCode: '7005',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
    affiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 21 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'greendale-secondary-school': {
    slug: 'greendale-secondary-school',
    moeSchoolCode: '3074',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'greenridge-secondary-school': {
    slug: 'greenridge-secondary-school',
    moeSchoolCode: '3051',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'guangyang-secondary-school': {
    slug: 'guangyang-secondary-school',
    moeSchoolCode: '3238',
    nonAffiliated: {
      postingGroup3: { first: { score: 9 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'hai-sing-catholic-school': {
    slug: 'hai-sing-catholic-school',
    moeSchoolCode: '7031',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 15 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'hillgrove-secondary-school': {
    slug: 'hillgrove-secondary-school',
    moeSchoolCode: '3048',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'holy-innocents-high-school': {
    slug: 'holy-innocents-high-school',
    moeSchoolCode: '7108',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 22 } },
      postingGroup2: { first: { score: 23 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'hougang-secondary-school': {
    slug: 'hougang-secondary-school',
    moeSchoolCode: '3046',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'hua-yi-secondary-school': {
    slug: 'hua-yi-secondary-school',
    moeSchoolCode: '3226',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'hwa-chong-institution': {
    slug: 'hwa-chong-institution',
    moeSchoolCode: '0806',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4, hclGrade: 'D' }, last: { score: 6, hclGrade: 'M' } },
    },
  },
  'junyuan-secondary-school': {
    slug: 'junyuan-secondary-school',
    moeSchoolCode: '3608',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 21 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'jurong-secondary-school': {
    slug: 'jurong-secondary-school',
    moeSchoolCode: '3211',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'jurong-west-secondary-school': {
    slug: 'jurong-west-secondary-school',
    moeSchoolCode: '3068',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'jurongville-secondary-school': {
    slug: 'jurongville-secondary-school',
    moeSchoolCode: '3063',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'juying-secondary-school': {
    slug: 'juying-secondary-school',
    moeSchoolCode: '3066',
    nonAffiliated: {
      postingGroup3: { first: { score: 17 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'kent-ridge-secondary-school': {
    slug: 'kent-ridge-secondary-school',
    moeSchoolCode: '3619',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'kranji-secondary-school': {
    slug: 'kranji-secondary-school',
    moeSchoolCode: '3065',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'kuo-chuan-presbyterian-secondary-school': {
    slug: 'kuo-chuan-presbyterian-secondary-school',
    moeSchoolCode: '7028',
    nonAffiliated: {
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
    affiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'loyang-view-secondary-school': {
    slug: 'loyang-view-secondary-school',
    moeSchoolCode: '3077',
    nonAffiliated: {
      postingGroup3: { first: { score: 16 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'manjusri-secondary-school': {
    slug: 'manjusri-secondary-school',
    moeSchoolCode: '7307',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 21 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
    affiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'maris-stella-high-school': {
    slug: 'maris-stella-high-school',
    moeSchoolCode: '7111',
    nonAffiliated: {
      postingGroup3: { first: { score: 7, hclGrade: 'M' }, last: { score: 12 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 4, hclGrade: 'M' }, last: { score: 16 } },
    },
  },
  'marsiling-secondary-school': {
    slug: 'marsiling-secondary-school',
    moeSchoolCode: '3615',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'mayflower-secondary-school': {
    slug: 'mayflower-secondary-school',
    moeSchoolCode: '3031',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 17 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'meridian-secondary-school': {
    slug: 'meridian-secondary-school',
    moeSchoolCode: '3076',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'methodist-girls-school-secondary': {
    slug: 'methodist-girls-school-secondary',
    moeSchoolCode: '7030',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 6 } },
      postingGroup3: { first: { score: 6 }, last: { score: 8 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 17 } },
    },
  },
  'montfort-secondary-school': {
    slug: 'montfort-secondary-school',
    moeSchoolCode: '7011',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 22 } },
      postingGroup2: { first: { score: 23 }, last: { score: 25 } },
      postingGroup1: { first: { score: 27 }, last: { score: 29 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 24 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'nan-chiau-high-school': {
    slug: 'nan-chiau-high-school',
    moeSchoolCode: '7112',
    nonAffiliated: {
      postingGroup3: { first: { score: 4, hclGrade: 'M' }, last: { score: 11, hclGrade: 'M' } },
    },
  },
  'nan-hua-high-school': {
    slug: 'nan-hua-high-school',
    moeSchoolCode: '3047',
    nonAffiliated: {
      postingGroup3: { first: { score: 5, hclGrade: 'M' }, last: { score: 10, hclGrade: 'M' } },
    },
  },
  'nanyang-girls-high-school': {
    slug: 'nanyang-girls-high-school',
    moeSchoolCode: '7114',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4, hclGrade: 'D' }, last: { score: 6, hclGrade: 'M' } },
    },
    affiliated: {
      integratedProgramme: { first: { score: 4, hclGrade: 'D' }, last: { score: 8, hclGrade: 'M' } },
    },
  },
  'national-junior-college': {
    slug: 'national-junior-college',
    moeSchoolCode: '0701',
    nonAffiliated: {
      integratedProgramme: { first: { score: 5 }, last: { score: 8 } },
    },
  },
  'naval-base-secondary-school': {
    slug: 'naval-base-secondary-school',
    moeSchoolCode: '3214',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 19 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'new-town-secondary-school': {
    slug: 'new-town-secondary-school',
    moeSchoolCode: '3507',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'ngee-ann-secondary-school': {
    slug: 'ngee-ann-secondary-school',
    moeSchoolCode: '7310',
    nonAffiliated: {
      postingGroup3: { first: { score: 6 }, last: { score: 12 } },
      postingGroup2: { first: { score: 21 }, last: { score: 21 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 16 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'north-vista-secondary-school': {
    slug: 'north-vista-secondary-school',
    moeSchoolCode: '3071',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'northbrooks-secondary-school': {
    slug: 'northbrooks-secondary-school',
    moeSchoolCode: '3612',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'northland-secondary-school': {
    slug: 'northland-secondary-school',
    moeSchoolCode: '3058',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'orchid-park-secondary-school': {
    slug: 'orchid-park-secondary-school',
    moeSchoolCode: '3605',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 19 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'outram-secondary-school': {
    slug: 'outram-secondary-school',
    moeSchoolCode: '3215',
    nonAffiliated: {
      postingGroup3: { first: { score: 9 }, last: { score: 17 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
  },
  'pasir-ris-crest-secondary-school': {
    slug: 'pasir-ris-crest-secondary-school',
    moeSchoolCode: '3613',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'pasir-ris-secondary-school': {
    slug: 'pasir-ris-secondary-school',
    moeSchoolCode: '3235',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 17 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'paya-lebar-methodist-girls-school-secondary': {
    slug: 'paya-lebar-methodist-girls-school-secondary',
    moeSchoolCode: '7026',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 10 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
    affiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 27 } },
    },
  },
  'pei-hwa-secondary-school': {
    slug: 'pei-hwa-secondary-school',
    moeSchoolCode: '3073',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'peicai-secondary-school': {
    slug: 'peicai-secondary-school',
    moeSchoolCode: '3232',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'peirce-secondary-school': {
    slug: 'peirce-secondary-school',
    moeSchoolCode: '3061',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'presbyterian-high-school': {
    slug: 'presbyterian-high-school',
    moeSchoolCode: '7308',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'punggol-secondary-school': {
    slug: 'punggol-secondary-school',
    moeSchoolCode: '3070',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 19 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'queenstown-secondary-school': {
    slug: 'queenstown-secondary-school',
    moeSchoolCode: '3508',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'queensway-secondary-school': {
    slug: 'queensway-secondary-school',
    moeSchoolCode: '3007',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 17 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'raffles-girls-school-secondary': {
    slug: 'raffles-girls-school-secondary',
    moeSchoolCode: '3008',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 6 } },
    },
  },
  'raffles-institution': {
    slug: 'raffles-institution',
    moeSchoolCode: '3009',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 6 } },
    },
  },
  'regent-secondary-school': {
    slug: 'regent-secondary-school',
    moeSchoolCode: '3618',
    nonAffiliated: {
      postingGroup3: { first: { score: 17 }, last: { score: 22 } },
      postingGroup2: { first: { score: 23 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'river-valley-high-school': {
    slug: 'river-valley-high-school',
    moeSchoolCode: '3103',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4, hclGrade: 'M' }, last: { score: 9, hclGrade: 'M' } },
    },
  },
  'riverside-secondary-school': {
    slug: 'riverside-secondary-school',
    moeSchoolCode: '3239',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'sembawang-secondary-school': {
    slug: 'sembawang-secondary-school',
    moeSchoolCode: '3606',
    nonAffiliated: {
      postingGroup3: { first: { score: 18 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'seng-kang-secondary-school': {
    slug: 'seng-kang-secondary-school',
    moeSchoolCode: '3607',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 19 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'serangoon-garden-secondary-school': {
    slug: 'serangoon-garden-secondary-school',
    moeSchoolCode: '3509',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 } },
    },
  },
  'serangoon-secondary-school': {
    slug: 'serangoon-secondary-school',
    moeSchoolCode: '3010',
    nonAffiliated: {
      postingGroup3: { first: { score: 16 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'singapore-chinese-girls-school': {
    slug: 'singapore-chinese-girls-school',
    moeSchoolCode: '7014',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 7 } },
      postingGroup3: { first: { score: 6 }, last: { score: 8 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 17 } },
    },
  },
  'springfield-secondary-school': {
    slug: 'springfield-secondary-school',
    moeSchoolCode: '3053',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 27 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'st-andrews-school-secondary': {
    slug: 'st-andrews-school-secondary',
    moeSchoolCode: '7015',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 9 } },
      postingGroup2: { first: { score: 21 }, last: { score: 21 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 9 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 } },
    },
  },
  'st-anthonys-canossian-secondary-school': {
    slug: 'st-anthonys-canossian-secondary-school',
    moeSchoolCode: '7016',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 15 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
    affiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 29 } },
    },
  },
  'st-gabriels-secondary-school': {
    slug: 'st-gabriels-secondary-school',
    moeSchoolCode: '7017',
    nonAffiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 15 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 22 } },
      postingGroup2: { first: { score: 23 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'st-hildas-secondary-school': {
    slug: 'st-hildas-secondary-school',
    moeSchoolCode: '7029',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 14 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 28 } },
    },
  },
  'st-josephs-institution': {
    slug: 'st-josephs-institution',
    moeSchoolCode: '7020',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 7 } },
      postingGroup3: { first: { score: 4 }, last: { score: 8 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 12 } },
    },
  },
  'st-margarets-school-secondary': {
    slug: 'st-margarets-school-secondary',
    moeSchoolCode: '7021',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 10 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'st-patricks-school': {
    slug: 'st-patricks-school',
    moeSchoolCode: '7022',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
    affiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'swiss-cottage-secondary-school': {
    slug: 'swiss-cottage-secondary-school',
    moeSchoolCode: '3304',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
  },
  'tampines-secondary-school': {
    slug: 'tampines-secondary-school',
    moeSchoolCode: '3037',
    nonAffiliated: {
      postingGroup3: { first: { score: 13 }, last: { score: 19 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'tanjong-katong-girls-school': {
    slug: 'tanjong-katong-girls-school',
    moeSchoolCode: '3013',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
    },
  },
  'tanjong-katong-secondary-school': {
    slug: 'tanjong-katong-secondary-school',
    moeSchoolCode: '3512',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
    },
  },
  'temasek-junior-college': {
    slug: 'temasek-junior-college',
    moeSchoolCode: '0702',
    nonAffiliated: {
      integratedProgramme: { first: { score: 4 }, last: { score: 8 } },
    },
  },
  'temasek-secondary-school': {
    slug: 'temasek-secondary-school',
    moeSchoolCode: '3030',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 11 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 26 } },
    },
  },
  'unity-secondary-school': {
    slug: 'unity-secondary-school',
    moeSchoolCode: '3611',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'victoria-school': {
    slug: 'victoria-school',
    moeSchoolCode: '3014',
    nonAffiliated: {
      integratedProgramme: { first: { score: 5 }, last: { score: 8 } },
      postingGroup3: { first: { score: 6 }, last: { score: 9 } },
    },
  },
  'west-spring-secondary-school': {
    slug: 'west-spring-secondary-school',
    moeSchoolCode: '3067',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 17 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'westwood-secondary-school': {
    slug: 'westwood-secondary-school',
    moeSchoolCode: '3620',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'whitley-secondary-school': {
    slug: 'whitley-secondary-school',
    moeSchoolCode: '3015',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 26 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'woodgrove-secondary-school': {
    slug: 'woodgrove-secondary-school',
    moeSchoolCode: '3616',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 19 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'woodlands-ring-secondary-school': {
    slug: 'woodlands-ring-secondary-school',
    moeSchoolCode: '3604',
    nonAffiliated: {
      postingGroup3: { first: { score: 17 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'woodlands-secondary-school': {
    slug: 'woodlands-secondary-school',
    moeSchoolCode: '3041',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 22 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'xinmin-secondary-school': {
    slug: 'xinmin-secondary-school',
    moeSchoolCode: '3050',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'yio-chu-kang-secondary-school': {
    slug: 'yio-chu-kang-secondary-school',
    moeSchoolCode: '3222',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 22 } },
      postingGroup2: { first: { score: 23 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 30 }, unfilledVacancies: true },
    },
  },
  'yishun-secondary-school': {
    slug: 'yishun-secondary-school',
    moeSchoolCode: '3020',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'yishun-town-secondary-school': {
    slug: 'yishun-town-secondary-school',
    moeSchoolCode: '3045',
    nonAffiliated: {
      postingGroup3: { first: { score: 5 }, last: { score: 13 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'yuan-ching-secondary-school': {
    slug: 'yuan-ching-secondary-school',
    moeSchoolCode: '3223',
    nonAffiliated: {
      postingGroup3: { first: { score: 12 }, last: { score: 18 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'yuhua-secondary-school': {
    slug: 'yuhua-secondary-school',
    moeSchoolCode: '3019',
    nonAffiliated: {
      postingGroup3: { first: { score: 15 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 28 } },
    },
  },
  'yusof-ishak-secondary-school': {
    slug: 'yusof-ishak-secondary-school',
    moeSchoolCode: '3307',
    nonAffiliated: {
      postingGroup3: { first: { score: 7 }, last: { score: 15 } },
      postingGroup2: { first: { score: 21 }, last: { score: 22 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'yuying-secondary-school': {
    slug: 'yuying-secondary-school',
    moeSchoolCode: '7027',
    nonAffiliated: {
      postingGroup3: { first: { score: 11 }, last: { score: 22 } },
      postingGroup2: { first: { score: 22 }, last: { score: 25 } },
      postingGroup1: { first: { score: 25 }, last: { score: 29 } },
    },
  },
  'zhenghua-secondary-school': {
    slug: 'zhenghua-secondary-school',
    moeSchoolCode: '3617',
    nonAffiliated: {
      postingGroup3: { first: { score: 14 }, last: { score: 20 } },
      postingGroup2: { first: { score: 21 }, last: { score: 24 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
  'zhonghua-secondary-school': {
    slug: 'zhonghua-secondary-school',
    moeSchoolCode: '3240',
    nonAffiliated: {
      postingGroup3: { first: { score: 8 }, last: { score: 12 } },
      postingGroup2: { first: { score: 21 }, last: { score: 23 } },
      postingGroup1: { first: { score: 25 }, last: { score: 27 } },
    },
  },
};

export function getSchoolCutOffPoints(slug: string): SchoolCutOffPoints | undefined {
  return SCHOOL_CUT_OFF_POINTS[slug];
}

/** `8`, or `8 (M)` where MOE published an HCL tie-break grade. */
export function formatCutOffBound(bound: CutOffBound): string {
  return bound.hclGrade ? `${bound.score} (${bound.hclGrade})` : String(bound.score);
}

/** `6 (D)–8 (M)`, using an en dash. */
export function formatCutOffRange(range: CutOffRange): string {
  return `${formatCutOffBound(range.first)}–${formatCutOffBound(range.last)}`;
}

/** The populated posting groups, most selective first. */
export function listPostingGroups(
  groups: PostingGroupCutOffs
): { key: PostingGroupKey; label: string; range: CutOffRange }[] {
  return POSTING_GROUP_ORDER.filter(key => groups[key]).map(key => ({
    key,
    label: POSTING_GROUP_LABELS[key],
    range: groups[key] as CutOffRange,
  }));
}

/**
 * The cut-off point to lead with: the most selective posting group the school
 * admits through, preferring non-affiliated because that is the number a
 * searcher without a feeder-school claim is looking for.
 */
export function getMostSelectiveCutOff(
  cutOffs: SchoolCutOffPoints
): { key: PostingGroupKey; label: string; range: CutOffRange; affiliated: boolean } | undefined {
  const preferred = listPostingGroups(cutOffs.nonAffiliated)[0];
  if (preferred) return { ...preferred, affiliated: false };
  const fallback = cutOffs.affiliated && listPostingGroups(cutOffs.affiliated)[0];
  return fallback ? { ...fallback, affiliated: true } : undefined;
}
